import React, { useCallback, useState } from 'react';
import { Slider } from 'antd';
import { SoundFilled } from '@ant-design/icons';
import styles from './VideoPlayer.module.scss';

interface VideoControlsProps {
  isPlaying: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  fps?: number;
  currentFrameIndex?: number;
  onPlayPause: () => void;
  onVolumeChange: (value: number) => void;
  onFullScreen: () => void;
  isLooping?: boolean;
  onToggleLoop?: () => void;
  /** Tam ekranın solunda: çizim araçları */
  toolbarSlot?: React.ReactNode;
  /** Compare + landscape: yorum çekmecesi aç/kapa (süre göstergesinin yanı) */
  compareCommentToggleSlot?: React.ReactNode;
}

const PauseGlyph = () => (
  <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden className={styles.controlGlyph}>
    <rect x="5" y="4" width="4" height="12" rx="1" fill="currentColor" />
    <rect x="11" y="4" width="4" height="12" rx="1" fill="currentColor" />
  </svg>
);

const PlayGlyph = () => (
  <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden className={styles.controlGlyph}>
    <path
      fill="currentColor"
      d="M17.6915 9.57956L3.32083 2.11623C2.78495 1.83897 2 2.08896 2 2.53667V17.4633C2 17.911 2.78495 18.161 3.32083 17.8838L17.6915 10.4204C18.1028 10.2068 18.1028 9.79319 17.6915 9.57956Z"
    />
  </svg>
);

type ShortcutHintId = 'play' | 'loop' | 'volume' | 'fullscreen';

const LoopGlyph = () => (
  <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden className={styles.controlGlyph}>
    <path
      fill="currentColor"
      d="M2.16662 10.8333H3.49128C3.58292 10.8333 3.6579 10.7583 3.6579 10.6667V5.34792H14.6675V6.86042C14.6675 6.9 14.68 6.9375 14.705 6.96875C14.7191 6.98675 14.7365 7.00179 14.7564 7.013C14.7762 7.02422 14.7981 7.03139 14.8208 7.0341C14.8434 7.03681 14.8664 7.03501 14.8884 7.0288C14.9103 7.02259 14.9308 7.0121 14.9487 6.99792L17.9333 4.65417C18.0229 4.55 18.0083 4.43959 17.9333 4.37917L14.9487 2.03751C14.918 2.01293 14.8797 1.99969 14.8404 2.00001C14.7446 2.00001 14.6655 2.07917 14.6655 2.17501V3.68751H3.49336C2.67066 3.68751 2 4.35834 2 5.18334V10.6667C2 10.7583 2.07498 10.8333 2.16662 10.8333ZM17.8292 9.16667H16.5046C16.4129 9.16667 16.3379 9.24167 16.3379 9.33334V14.6521H5.3283V13.1396C5.3283 13.1 5.3158 13.0625 5.29081 13.0313C5.27678 13.0133 5.25932 12.9982 5.23945 12.987C5.21958 12.9758 5.19769 12.9686 5.17504 12.9659C5.15239 12.9632 5.12942 12.965 5.10747 12.9712C5.08551 12.9774 5.065 12.9879 5.04712 13.0021L2.06248 15.3458C1.97292 15.45 1.9875 15.5604 2.06248 15.6208L5.04712 17.9625C5.07836 17.9875 5.11586 18 5.15543 18C5.25124 18 5.33038 17.9208 5.33038 17.825V16.3125H16.5066C17.3293 16.3125 18 15.6417 18 14.8167V9.33334C17.9958 9.24167 17.9209 9.16667 17.8292 9.16667Z"
    />
  </svg>
);

const VideoControls: React.FC<VideoControlsProps> = ({
  isPlaying,
  volume,
  currentTime,
  duration: _duration,
  fps = 30,
  currentFrameIndex,
  onPlayPause,
  onVolumeChange,
  onFullScreen,
  isLooping,
  onToggleLoop,
  toolbarSlot,
  compareCommentToggleSlot,
}) => {
  const formatTime = (time: number | undefined) => {
    if (time === undefined || isNaN(time) || time < 0) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const effectiveFps = fps > 0 ? fps : 30;
  const currentFrame =
    typeof currentFrameIndex === 'number' && Number.isFinite(currentFrameIndex)
      ? Math.max(0, Math.round(currentFrameIndex))
      : Math.max(0, Math.floor(currentTime * effectiveFps));

  const [openShortcut, setOpenShortcut] = useState<ShortcutHintId | null>(null);

  const bindShortcutRegion = useCallback((id: ShortcutHintId) => {
    const onPointerEnter = () => setOpenShortcut(id);
    const onPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
      const next = e.relatedTarget as Node | null;
      if (!next || !e.currentTarget.contains(next)) {
        setOpenShortcut((h) => (h === id ? null : h));
      }
    };
    return { onPointerEnter, onPointerLeave };
  }, []);

  const hintClass = (id: ShortcutHintId, align: 'start' | 'end') =>
    [
      styles.controlShortcutHint,
      align === 'start' ? styles.controlShortcutHintAlignStart : styles.controlShortcutHintAlignEnd,
      openShortcut === id ? styles.controlShortcutHintVisible : '',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div className={styles.bottomControls}>
      <div className={styles.bottomControlsMain}>
        <div className={styles.controlWithShortcut} {...bindShortcutRegion('play')}>
          <button
            type="button"
            className={`${styles.iconControlBtn} ${styles.iconControlBtnMedia} ${isPlaying ? styles.iconControlBtnActive : ''}`}
            onClick={onPlayPause}
            onFocus={() => setOpenShortcut('play')}
            onBlur={(e) => {
              const parent = e.currentTarget.parentElement;
              const rel = e.relatedTarget as Node | null;
              if (!parent || !rel || !parent.contains(rel)) {
                setOpenShortcut((h) => (h === 'play' ? null : h));
              }
            }}
            aria-label={isPlaying ? 'Pause (K or Space)' : 'Play (K or Space)'}
          >
            {isPlaying ? (
              <PauseGlyph />
            ) : (
              <PlayGlyph />
            )}
          </button>
          <span className={hintClass('play', 'start')} aria-hidden>
            <span className={styles.controlShortcutHintText}>{isPlaying ? 'Pause' : 'Play'}</span>
            <span className={styles.controlShortcutHintKey}>K</span>
          </span>
        </div>
        <div className={styles.controlWithShortcut} {...bindShortcutRegion('loop')}>
          <button
            type="button"
            className={`${styles.iconControlBtn} ${styles.iconControlBtnMedia} ${isLooping ? styles.iconControlBtnActive : ''}`}
            onClick={() => onToggleLoop?.()}
            onFocus={() => setOpenShortcut('loop')}
            onBlur={(e) => {
              const parent = e.currentTarget.parentElement;
              const rel = e.relatedTarget as Node | null;
              if (!parent || !rel || !parent.contains(rel)) {
                setOpenShortcut((h) => (h === 'loop' ? null : h));
              }
            }}
            aria-label={isLooping ? 'Turn off loop (L)' : 'Turn on loop (L)'}
          >
            <LoopGlyph />
          </button>
          <span className={hintClass('loop', 'start')} aria-hidden>
            <span className={styles.controlShortcutHintText}>Loop</span>
            <span className={styles.controlShortcutHintKey}>L</span>
          </span>
        </div>
        {compareCommentToggleSlot ? (
          <div className={styles.bottomControlsCommentToggle}>{compareCommentToggleSlot}</div>
        ) : null}
        <div
          className={`${styles.volumeRow} ${styles.controlWithShortcut}`}
          {...bindShortcutRegion('volume')}
          onFocusCapture={() => setOpenShortcut('volume')}
          onBlurCapture={(e) => {
            const rel = e.relatedTarget as Node | null;
            if (!rel || !e.currentTarget.contains(rel)) {
              setOpenShortcut((h) => (h === 'volume' ? null : h));
            }
          }}
        >
          <SoundFilled className={styles.volumeIcon} aria-hidden />
          <Slider
            className={styles.volumeSlider}
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={onVolumeChange}
            tooltip={{ formatter: null }}
          />
          <span className={hintClass('volume', 'start')} aria-hidden>
            <span className={styles.controlShortcutHintText}>Mute</span>
            <span className={styles.controlShortcutHintKey}>M</span>
          </span>
        </div>
        <span className={styles.timeDisplay}>
          {formatTime(currentTime)}/{currentFrame}
        </span>
      </div>
      <div className={styles.bottomControlsRight}>
        {toolbarSlot ? (
          <div className={styles.bottomControlsToolbar}>{toolbarSlot}</div>
        ) : null}
        <div
          className={`${styles.controlWithShortcut} ${styles.bottomControlsExpandSlot}`}
          {...bindShortcutRegion('fullscreen')}
        >
          <button
            type="button"
            className={`${styles.iconControlBtn} ${styles.iconControlBtnMedia}`}
            onClick={onFullScreen}
            onFocus={() => setOpenShortcut('fullscreen')}
            onBlur={(e) => {
              const parent = e.currentTarget.parentElement;
              const rel = e.relatedTarget as Node | null;
              if (!parent || !rel || !parent.contains(rel)) {
                setOpenShortcut((h) => (h === 'fullscreen' ? null : h));
              }
            }}
            aria-label="Full screen (F)"
          >
            <img src="/images/expand.svg" alt="" className={styles.controlGlyphImg} width={20} height={20} />
          </button>
          <span className={hintClass('fullscreen', 'end')} aria-hidden>
            <span className={styles.controlShortcutHintText}>Full</span>
            <span className={styles.controlShortcutHintKey}>F</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default VideoControls; 